import { QuestionCircleOutlined, LogoutOutlined, UserOutlined, LockOutlined } from '@ant-design/icons';
import { SelectLang as UmiSelectLang, useModel, history } from '@umijs/max';
import { Dropdown, Avatar, message, Modal, Form, Input } from 'antd';
import React, { useState } from 'react';
import { useIntl } from 'umi';
import NoticeIcon from './NoticeIcon';
import { authService } from '@/services/authService';
import type { MenuProps } from 'antd';

export type SiderTheme = 'light' | 'dark';

export const SelectLang = () => {
  return (
    <UmiSelectLang
      style={{
        padding: 4,
      }}
    />
  );
};

export const Question = () => {
  return (
    <div
      style={{
        display: 'flex',
        height: 26,
      }}
    >
      <QuestionCircleOutlined />
    </div>
  );
};

const RightContent: React.FC = () => {
  const intl = useIntl();
  const { initialState, setInitialState } = useModel('@@initialState');
  const currentUser = initialState?.currentUser;
  const [changePasswordVisible, setChangePasswordVisible] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [form] = Form.useForm();

  const handleLogout = async () => {
    try {
      Modal.confirm({
        title: intl.formatMessage({ id: 'menu.account.logout.confirm' }),
        content: intl.formatMessage({ id: 'menu.account.logout.confirm.content' }),
        onOk: async () => {
          await authService.logout();
          await setInitialState((s) => ({ ...s, currentUser: undefined }));
          history.push('/login');
          message.success(intl.formatMessage({ id: 'menu.account.logout.success' }));
        },
      });
    } catch (error) {
      console.error('Logout failed:', error);
      message.error(intl.formatMessage({ id: 'menu.account.logout.failed' }));
    }
  };

  const handleChangePassword = async () => {
    try {
      const values = await form.validateFields();
      setConfirmLoading(true);
      
      // 检查新密码和确认密码是否匹配
      if (values.newPassword !== values.confirmPassword) {
        message.error(intl.formatMessage({ id: 'menu.account.changePassword.passwordMismatch' }));
        setConfirmLoading(false);
        return;
      }
      
      const response = await authService.changePassword(values.currentPassword, values.newPassword);
      
      if (response.success) {
        message.success(intl.formatMessage({ id: 'menu.account.changePassword.success' }));
        form.resetFields();
        setChangePasswordVisible(false);
      } else {
        message.error(response.message || intl.formatMessage({ id: 'menu.account.changePassword.failed' }));
      }
    } catch (error: any) {
      if (error.response?.status === 401) {
        message.error(intl.formatMessage({ id: 'menu.account.changePassword.incorrectPassword' }));
      } else {
        message.error(intl.formatMessage({ id: 'menu.account.changePassword.failed' }));
      }
      console.error('修改密码失败:', error);
    } finally {
      setConfirmLoading(false);
    }
  };

  const items: MenuProps['items'] = [
    {
      key: 'email',
      label: currentUser?.email,
    },
    {
      key: 'changePassword',
      icon: <LockOutlined />,
      label: intl.formatMessage({ id: 'menu.account.changePassword' }),
      onClick: () => {
        setChangePasswordVisible(true);
      },
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: intl.formatMessage({ id: 'menu.account.logout' }),
      onClick: handleLogout,
    },
  ];

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {/* <NoticeIcon /> */}
      <SelectLang />
      {currentUser && (
        <Dropdown menu={{ items }} placement="bottomRight">
          <span style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
            <Avatar size="small" icon={<UserOutlined />} style={{ marginRight: 10, marginLeft: 6 }} />
          </span>
        </Dropdown>
      )}
     
      <Modal
        title={intl.formatMessage({ id: 'menu.account.changePassword' })}
        open={changePasswordVisible}
        onOk={handleChangePassword}
        onCancel={() => setChangePasswordVisible(false)}
        confirmLoading={confirmLoading}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="currentPassword"
            label={intl.formatMessage({ id: 'menu.account.changePassword.currentPassword' })}
            rules={[
              {
                required: true,
                message: intl.formatMessage({ id: 'menu.account.changePassword.currentPassword.required' }),
              },
            ]}
          >
            <Input.Password />
          </Form.Item>
          
          <Form.Item
            name="newPassword"
            label={intl.formatMessage({ id: 'menu.account.changePassword.newPassword' })}
            rules={[
              {
                required: true,
                message: intl.formatMessage({ id: 'menu.account.changePassword.newPassword.required' }),
              },
              {
                min: 6,
                message: intl.formatMessage({ id: 'menu.account.changePassword.password.tooShort' }),
              },
            ]}
          >
            <Input.Password />
          </Form.Item>
          
          <Form.Item
            name="confirmPassword"
            label={intl.formatMessage({ id: 'menu.account.changePassword.confirmPassword' })}
            dependencies={['newPassword']}
            rules={[
              {
                required: true,
                message: intl.formatMessage({ id: 'menu.account.changePassword.confirmPassword.required' }),
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(intl.formatMessage({ id: 'menu.account.changePassword.passwordMismatch' })));
                },
              }),
            ]}
          >
            <Input.Password />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default RightContent;
export { NoticeIcon };
